import styles from './overview.module.css';
import { Accordion } from '@chakra-ui/react';
import LevelsImage from './level-image';
import {
	formatAge,
	formatAsia,
	formatLevels,
	formatTimeFrame,
} from '@scitrials-sst/core/Next/lib';
import MyMarkdown from '@/components/my-markdown/my-markdown';

function OverviewQuestion({
	index,
	question,
	children,
}: {
	index: string;
	question: string;
	children: React.ReactNode;
}) {
	return (
		<Accordion.Item value={index} borderBottom={'none'}>
			<Accordion.ItemTrigger padding={'1rem 0.5rem'} cursor={'pointer'}>
				<Accordion.ItemIndicator color={'#446cb3'} />
				<h1 className={styles['accord-title']}>{question}</h1>
			</Accordion.ItemTrigger>

			<Accordion.ItemContent>
				<Accordion.ItemBody>
					<div className={styles['accord-body']}>{children}</div>
				</Accordion.ItemBody>
			</Accordion.ItemContent>
		</Accordion.Item>
	);
}

export default function Overview({
	answers,
	goTo,
}: {
	answers: [
		{ whatIsIt: string },
		{
			whoCanTakePart: string;
			levelInt: number;
			asiaInt: number;
			timeframeInt: number;
			minimumAge: string;
			maximumAge: string;
			sex: string;
		},
		{ whatsInvolved: string },
		{
			locations: any[];
			watch: string[];
			nctid: string;
		},
		{ pay: string },
		{ costs: string },
		{
			controlGroup: string;
			controlGroupFullTreatment: string;
		},
		{ regulation: string }
	];
	goTo: (value: 'details' | 'locations') => void;
}) {
	return (
		<Accordion.Root
			multiple
			marginTop={'1rem'}
			defaultValue={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
		>
			{/* 1 */}
			{answers[0]?.whatIsIt && (
				<OverviewQuestion index={`0`} question='What is the trial about?'>
					<p>{answers[0]?.whatIsIt}</p>
				</OverviewQuestion>
			)}

			{/* 2 */}
			<OverviewQuestion index={`1`} question='Who can take part?'>
				<div className=''>
					<MyMarkdown text={answers[1]?.whoCanTakePart ?? ''} />

					<div className={styles.whoCanTakePart}>
						<div className={styles['whoCanTakePart-title']}>Injury Details</div>
						<div className={styles['whoCanTakePart-body']}>
							<div className={styles['whoCanTakePart-text']}>
								<p className={styles['cantakepart-title']}>Injury Levels</p>
								<p className={styles['cantakepart-text']}>
									{formatLevels(answers[1]?.levelInt).toUpperCase()}
								</p>
								<p className={styles['cantakepart-title']}>AIS Grade</p>
								<p className={styles['cantakepart-text']}>
									{formatAsia(answers[1].asiaInt).toUpperCase()}
								</p>
								<p className={styles['cantakepart-title']}>Time since injury</p>
								<p className={styles['cantakepart-text']}>
									{formatTimeFrame(answers[1].timeframeInt)}
								</p>
								<p className={styles['cantakepart-title']}>Gender</p>
								<p className={styles['cantakepart-text']}>
									{answers[1]?.sex ?? 'All'}
								</p>
								<p className={styles['cantakepart-title']}>Age</p>
								<p className={styles['cantakepart-text']}>
									{formatAge(answers[1]?.minimumAge, answers[1]?.maximumAge)}
								</p>
							</div>
							<LevelsImage levels={formatLevels(answers[1]?.levelInt)} />
						</div>
					</div>

					<div>
						<p>
							The full details are provided on the{' '}
							<button
								className={styles.redirect}
								onClick={() => goTo('details')}
							>
								Trial Details page
							</button>
						</p>
					</div>
				</div>
			</OverviewQuestion>

			{/* 3 */}
			<OverviewQuestion
				index={`2`}
				question='What will be required for the trial?'
			>
				<div className=''>
					<MyMarkdown text={answers[2]?.whatsInvolved ?? ''} />

					<p>
						The full details are provided on the{' '}
						<button className={styles.redirect} onClick={() => goTo('details')}>
							Trial Details page
						</button>
					</p>
				</div>
			</OverviewQuestion>

			{/* 4 */}
			{answers[3] && (
				<OverviewQuestion
					index={`3`}
					question='Where is it and how do I sign up?'
				>
					{answers[3]?.locations?.length > 0 ? (
						<p>
							You can find out about the different locations where the trial is
							being run and sign up on the{' '}
							<button
								className={styles.redirect}
								onClick={() => goTo('locations')}
							>
								Locations page
							</button>
						</p>
					) : (
						<>
							<span>The trial has not yet started recruiting, </span>
							{answers[3]?.watch?.indexOf(answers[3]?.nctid) !== -1 ? (
								<>
									as you are{' '}
									<span
										style={{
											color: '#446cb3',
										}}
									>
										Following
									</span>{' '}
									the trial we will let you know when this changes.
								</>
							) : (
								<>
									please{' '}
									<span
										style={{
											color: '#446cb3',
										}}
									>
										Follow The Trial
									</span>{' '}
									and we will let you know when this changes.
								</>
							)}
						</>
					)}
				</OverviewQuestion>
			)}

			{/* 5 */}
			<OverviewQuestion index={`4`} question='Do I have to pay for this trial?'>
				{answers[4]?.pay ? (
					<p>{answers[4]?.pay}</p>
				) : (
					<p>
						There should be no charge for participating in this trial. Requests
						for payment are not allowed in valid clinical trial programs. If you
						apply for this trial we advise you to ask for a confirmation from
						the principal investigator, that there will be no charge for the
						trial, follow-up or any rehab.
					</p>
				)}
			</OverviewQuestion>

			{/* 6 */}
			<OverviewQuestion
				index={`5`}
				question='Will I have my travel costs reimbursed?'
			>
				{answers[5]?.costs ? (
					<p>{answers[5]?.costs}</p>
				) : (
					<p>You can ask the trial organiser about this when applying.</p>
				)}
			</OverviewQuestion>

			{/* 7 */}
			{answers[6] && (
				<OverviewQuestion
					index={`6`}
					question='Is there a control group for this trial?'
				>
					{answers[6]?.controlGroup === 'no' ? (
						<p>
							There is no control group, which means that all participants
							including yourself will be receiving the full study program.
						</p>
					) : (
						<>
							<p>
								There is a control group who will receive a placebo or not
								undertake the full treatment.
							</p>
							{answers[6]?.controlGroupFullTreatment === 'yes' ? (
								<p>
									If you enter the control group you will be offered the full
									treatment at the end of the trial.
								</p>
							) : answers[6]?.controlGroupFullTreatment === 'no' ? (
								<p>
									If you enter the control group you will not be automatically
									offered the full treatment at the end of the trial.
								</p>
							) : answers[6]?.controlGroupFullTreatment === 'maybe' ? (
								<p>
									It is currently undecided if you will be offered the full
									treatment at the end of the trial.
								</p>
							) : null}
						</>
					)}
				</OverviewQuestion>
			)}

			{/* 8 */}
			<OverviewQuestion
				index={`7`}
				question='What if I have already participated in a clinical trial?'
			>
				<p>
					Depending on the previous trial you may be able to participate. When
					applying please let the investigator know and they will be able to
					tell you.
				</p>
			</OverviewQuestion>

			{/* 9 */}
			{answers[7]?.regulation && (
				<OverviewQuestion
					index={`8`}
					question='Is this study registered as a clinical trial with an appropriate qualified regulatory body?'
				>
					<p>{answers[7]?.regulation}</p>
				</OverviewQuestion>
			)}

			{/* 10 */}
			<OverviewQuestion
				index={`9`}
				question='Are there safety risks associated with this experimental treatment?'
			>
				<p>
					As with all clinical trials there is an element of risk, however all
					relevant safety checks and procedures should be followed by the trial.
					You should discuss your concerns with the trial investigator on
					applying.
				</p>
			</OverviewQuestion>
		</Accordion.Root>
	);
}
